import { IconTrash } from '@tabler/icons-react'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogTrigger,
} from '../../atoms/alert-dialog'
import { PrimaryButton } from '../../atoms/buttons/PrimaryButton'
import { FC, useCallback, useState } from 'react'
import { Project } from 'src/types/projects'
import { Input } from '../../atoms/inputs/Input'
import { api } from 'src/api/root'
import { enqueueSnackbar } from 'notistack'
import Typography from '../../atoms/Typography'
import { formatCount } from '../../../enviroment/utils/formatCount'
import ExpandableList from '../../molecules/ExpandableList'

interface Props {
  project: Project
  requireConfirmation?: boolean
}

const DeleteProjectDialog: FC<Props> = ({ project, requireConfirmation = false }) => {
  const [opened, setOpened] = useState(false)
  const [step, setStep] = useState<'tenant-warning' | 'confirm'>(project.tenantCount > 0 ? 'tenant-warning' : 'confirm')
  const [projectName, setProjectName] = useState('')

  const handleOpen = useCallback(
    (open: boolean) => {
      setOpened(open)
      if (open) {
        setStep(project.tenantCount > 0 ? 'tenant-warning' : 'confirm')
        setProjectName('')
      }
    },
    [project.tenantCount],
  )

  const handleClose = useCallback(() => {
    setOpened(false)
    setProjectName('')
  }, [])

  const { data: currentTenants } = api.superAdmin.getCurrentTenantsOfProject.useQuery(project.id, {
    enabled: opened && project.tenantCount > 0,
  })

  const liveCount = currentTenants?.length ?? project.tenantCount
  const effectiveStep: 'tenant-warning' | 'confirm' = step === 'tenant-warning' && liveCount === 0 ? 'confirm' : step

  const { mutateAsync: deleteProjectAsync, isPending } = api.superAdmin.deleteProject.useMutation()

  const handleDelete = useCallback(async () => {
    try {
      await deleteProjectAsync(project.id)
      enqueueSnackbar(`Project "${project.name}" deleted successfully`, { variant: 'success' })
      handleClose()
    } catch (error) {
      console.log(error)
    }
  }, [deleteProjectAsync, handleClose, project.id, project.name])

  const isDeleteDisabled = (requireConfirmation && projectName !== project.name) || isPending

  return (
    <AlertDialog open={opened} onOpenChange={handleOpen}>
      <AlertDialogTrigger className="ml-2 flex h-10 w-16 items-center justify-center rounded-full border border-red-ramo text-red-600 transition-all hover:cursor-pointer hover:bg-red-600 hover:text-white active:scale-95">
        <IconTrash size={24} />
      </AlertDialogTrigger>
      <AlertDialogContent>
        <div className="flex flex-col items-center gap-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFADAD]">
            <IconTrash className="h-6 w-6 text-red-600" />
          </div>
          <div className="flex w-full flex-col justify-center gap-4">
            <span className="text-center text-xl text-green-50">Delete Project</span>

            {effectiveStep === 'tenant-warning' ? (
              <>
                <Typography variant="base" className="text-center text-gray-50">
                  This project belongs to{' '}
                  <Typography variant="base-semibold" className="inline">
                    {formatCount(currentTenants ? currentTenants.length : project.tenantCount, 'tenant')}
                  </Typography>
                  . Are you sure you want to delete it?
                </Typography>

                <ExpandableList
                  items={currentTenants?.map((tenant) => ({ id: tenant.id, title: tenant.name, subtitle: tenant.id }))}
                />
              </>
            ) : requireConfirmation ? (
              <>
                <span className="text-center text-gray-50">
                  Please type <span className="font-semibold">{project.name}</span> to confirm deletion.
                </span>
                <Input
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="Enter project name"
                  autoComplete="off"
                />
              </>
            ) : (
              <span className="text-center text-gray-50">
                You&apos;re going to delete {project.name} project. Are you sure?
              </span>
            )}
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending} onClick={handleClose} className="w-40">
            Cancel
          </AlertDialogCancel>
          {effectiveStep === 'tenant-warning' ? (
            <PrimaryButton
              onClick={() => setStep('confirm')}
              className="w-40 bg-red-600 hover:bg-red-500 active:bg-red-500"
            >
              Continue
            </PrimaryButton>
          ) : (
            <PrimaryButton
              disabled={isDeleteDisabled}
              isLoading={isPending}
              onClick={handleDelete}
              className="w-40 bg-red-600 hover:bg-red-500 active:bg-red-500"
            >
              Delete
            </PrimaryButton>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default DeleteProjectDialog
